import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Alert, Spinner, Row, Col, ListGroup, Badge, Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FaFileUpload, FaCheck, FaExclamationTriangle, FaChartLine, FaSync, FaSyncAlt } from 'react-icons/fa';
import { useNotifications } from '../../context/NotificationContext';
import { submitToLender, getLenderMatchSettings } from '../../api/lenderApi';
import EnhancedLenderButton from './EnhancedLenderButton';
import EquipmentInformation from './EquipmentInformation';

// Custom styles for form labels
const labelStyle = {
  fontWeight: '600',
  color: '#495057',
  marginBottom: '0.25rem', // Reduced margin between label and input
  fontSize: '0.95rem'
};

/**
 * LenderSubmission Component
 * 
 * Handles the submission of contact and business data to various lenders.
 * Uses provided bank statements and application forms.
 * Shows submission status, results, and error messages.
 * Integrates with the notification system to display toast notifications.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.contactData - Contact information
 * @param {Object} props.opportunityData - Business opportunity data (renamed to businessData internally)
 * @param {string} [props.dealNotes=''] - Optional deal notes to include in email submissions
 * @param {Array} props.bankFiles - Array of bank statement files
 * @param {Array} props.appFiles - Array of application document files
 * @param {Array} props.quoteFiles - Array of equipment quote files for equipment financing
 * @param {Array} props.creditFiles - Array of credit files for equipment financing
 * @param {boolean} [props.disabled=false] - Whether submission is disabled
 * @param {string} [props.financingType='working-capital'] - Type of financing ('working-capital' or 'equipment-financing')
 * @param {Array} [props.preloadedLenders=[]] - Pre-filtered lenders for this financing type (eliminates loading delay)
 * @param {boolean} [props.lendersLoading=false] - Whether lenders are still being loaded
 * @returns {JSX.Element} LenderSubmission component
 */
const LenderSubmission = ({ 
  contactData, 
  opportunityData, 
  dealNotes = '',
  bankFiles = [], 
  appFiles = [],
  quoteFiles = [],
  creditFiles = [], 
  disabled = false,
  financingType = 'working-capital',
  preloadedLenders = [],
  lendersLoading = false
}) => {
  // Rename the prop internally for clarity
  const businessData = opportunityData;
  
  // Get the opportunity ID from the URL params
  const { opportunityId } = useParams();
  
  // Access notification context
  const { success, error: notifyError, info, warning } = useNotifications();
  
  // State for lenders (now using preloaded data)
  // Initialize with preloadedLenders to avoid initial empty state flash
  const [lenders, setLenders] = useState(preloadedLenders);

  // Log the opportunity ID when the component initializes
  useEffect(() => {
    console.log(`LenderSubmission component initialized with opportunityId: ${opportunityId}, financingType: ${financingType}`);
  }, [opportunityId, financingType]);
  
  // Use preloaded lenders when they become available or financing type changes
  useEffect(() => {
    console.log(`Using preloaded lenders for financing type: ${financingType}`);
    console.log(`Received ${preloadedLenders.length} preloaded lenders`);
    console.log(`Lenders loading: ${lendersLoading}`);
    
    // Only update lenders if we're not loading or if we have actual lenders
    // This prevents the brief "no lenders" flash when loading finishes but props haven't updated yet
    if (!lendersLoading || preloadedLenders.length > 0) {
      setLenders(preloadedLenders);
    }
    
    // Reset submission states when changing financing type
    setResults({});
    setErrors({});
    setSubmittingLenders({});
    setResult(null);
    setError(null);
    setCurrentLender(null);
    setSelectedLenders(new Set());
  }, [financingType, preloadedLenders, lendersLoading]);
  
  // State for submission status
  const [submitting, setSubmitting] = useState(false);
  const [currentLender, setCurrentLender] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // NEW: State for tracking multiple submissions
  const [submittingLenders, setSubmittingLenders] = useState({});
  const [results, setResults] = useState({});
  const [errors, setErrors] = useState({});
  
  // NEW: State for toggle mode and master submit
  const [selectedLenders, setSelectedLenders] = useState(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [masterSubmitting, setMasterSubmitting] = useState(false);
  
  // State for lender match functionality
  const [matchEnabled, setMatchEnabled] = useState(false);
  const [matchCriteria, setMatchCriteria] = useState([]);
  const [matchScores, setMatchScores] = useState({});

  const [loadingMatch, setLoadingMatch] = useState(false);
  const [lastScoreUpdate, setLastScoreUpdate] = useState(null);
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  
  // List of field IDs that contain numeric values but are stored as text
  const numericTextFields = [
    'AverageBankBalance',
    'AnnualRevenue',
    'RequestedAmount',
    'ContactOwnershipPercentage',
    'FederalTaxID',
    // Add any other fields that should be treated as numbers
  ];
  
  // Helper function to determine if a field should be treated as numeric
  const isNumericField = (fieldId) => {
    return numericTextFields.includes(fieldId);
  };
  
  // Load lender match settings on component mount
  useEffect(() => {
    const loadMatchSettings = async () => {
      try {
        setLoadingMatch(true);
        const settings = await getLenderMatchSettings();
        
        setMatchEnabled(settings.enabled);
        
        if (settings.enabled && settings.lenders && settings.lenders.length > 0) {
          setMatchCriteria(settings.lenders);
          
          // If enabled, calculate scores
          calculateMatchScores(settings.lenders);
        }
      } catch (err) {
        console.error('Error loading lender match settings:', err);
      } finally {
        setLoadingMatch(false);
      }
    };
    
    loadMatchSettings();
  }, []);
  
  // Recalculate match scores whenever business data changes
  useEffect(() => {
    if (matchEnabled && matchCriteria.length > 0 && businessData) {
      console.log('Business data changed, recalculating match scores');
      // Use a small delay to ensure all form data is processed
      const timer = setTimeout(() => {
        calculateMatchScores(matchCriteria);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [businessData, contactData, matchEnabled, matchCriteria]);
  
  // Force recalculation when specific fields are updated through the form
  const forceRecalculateScores = () => {
    if (matchEnabled && matchCriteria.length > 0) {
      console.log('Forcing recalculation of match scores');
      calculateMatchScores(matchCriteria);
    }
  };
  
  // Get the most current field value, checking the DOM if necessary
  const getCurrentFieldValue = (fieldId) => {
    // Special case for CumulativeOwnershipPercentage - calculate from primary and secondary owner percentages
    if (fieldId === 'CumulativeOwnershipPercentage') {
      // Get primary owner percentage
      const primaryOwnership = normalizeNumericValue(
        businessData?.ContactOwnershipPercentage || 
        document.querySelector('input[name="ContactOwnershipPercentage"], input[id="ContactOwnershipPercentage"]')?.value ||
        0
      );
      
      // Get secondary owner percentage
      const secondaryOwnership = normalizeNumericValue(
        businessData?.Contact2OwnershipPercentage || 
        document.querySelector('input[name="Contact2OwnershipPercentage"], input[id="Contact2OwnershipPercentage"]')?.value ||
        0
      );
      
      // Calculate cumulative ownership
      const cumulativeOwnership = primaryOwnership + secondaryOwnership;
      console.log(`Calculated cumulative ownership: ${primaryOwnership} + ${secondaryOwnership} = ${cumulativeOwnership}`);
      
      return cumulativeOwnership;
    }
    
    // First check businessData from props
    if (businessData && businessData[fieldId] !== undefined) {
      return businessData[fieldId];
    }
    
    // If not found or potentially stale, check for form input elements with matching ID or name
    try {
      // Look for input by field ID
      const inputElement = document.querySelector(`input[name="${fieldId}"], input[id="${fieldId}"]`);
      if (inputElement) {
        return inputElement.value;
      }
      
      // For select elements
      const selectElement = document.querySelector(`select[name="${fieldId}"], select[id="${fieldId}"]`);
      if (selectElement) {
        return selectElement.value;
      }
    } catch (err) {
      console.error('Error checking DOM for updated field value:', err);
    }
    
    // Fallback to businessData
    return businessData?.[fieldId];
  };
  
  // Evaluate each criterion with the most current data
  const evaluateCriterionWithCurrentData = (criterion) => {
    // Get the most up-to-date field value
    const fieldValue = getCurrentFieldValue(criterion.fieldId);
    return evaluateCriterion(criterion, fieldValue);
  };
  
  // Check if field is a date comparison field (BusinessStartDate or DateOfBirth)
  const isDateComparisonField = (fieldId) => {
    return fieldId === 'BusinessStartDate' || fieldId === 'DateOfBirth';
  };
  
  // Calculate the difference in months between two dates
  const getMonthDifference = (dateValue) => {
    if (!dateValue) return 0;
    
    try {
      // Parse the date from the YYYY-MM-DD format
      const [year, month, day] = String(dateValue).split('-').map(num => parseInt(num, 10));
      
      // Validate date components
      if (isNaN(year) || isNaN(month) || isNaN(day) || 
          year < 1900 || year > 2100 || 
          month < 1 || month > 12 || 
          day < 1 || day > 31) {
        console.error(`Invalid date format: ${dateValue}`);
        return 0;
      }
      
      // Create date objects
      const dateObj = new Date(year, month - 1, day); // month is 0-indexed in JS
      const currentDate = new Date();
      
      // Additional validation: ensure the date object was created correctly
      if (dateObj.toString() === 'Invalid Date') {
        console.error(`Could not create valid date from: ${dateValue}`);
        return 0;
      }
      
      // Calculate difference in months
      const monthDiff = 
        (currentDate.getFullYear() - dateObj.getFullYear()) * 12 + 
        (currentDate.getMonth() - dateObj.getMonth());
      
      console.log(`Date difference calculation: 
        Date value: ${dateValue}
        Parsed date: ${dateObj.toISOString()}
        Current date: ${currentDate.toISOString()}
        Difference in months: ${monthDiff}`);
      
      return monthDiff;
    } catch (err) {
      console.error('Error calculating month difference:', err);
      return 0;
    }
  };
  
  // Calculate match scores based on criteria and business data
  const calculateMatchScores = (lenderCriteria) => {
    console.log('Recalculating match scores with current business data:', businessData);
    const scores = {};
    
    // Go through each lender's criteria
    lenderCriteria.forEach(lender => {
      console.log(`Evaluating criteria for lender: ${lender.name} (${lender.lenderId})`);
      
      // Skip if no criteria defined
      if (!lender.criteria || lender.criteria.length === 0) {
        scores[lender.lenderId] = {
          score: 0,
          matched: [],
          missed: [],
          incomplete: [],
          disqualified: false
        };
        return;
      }
      
      let totalWeight = 0;
      let weightedScore = 0;
      const matched = [];
      const missed = [];
      const incomplete = [];
      let disqualified = false;
      
      // Evaluate each criterion
      lender.criteria.forEach(criterion => {
        // Find the most current field value
        const fieldValue = getCurrentFieldValue(criterion.fieldId);
        const weight = criterion.weight || 5;
        
        // Create an enhanced criterion object including the actual value
        const evaluatedCriterion = {
          ...criterion,
          actualValue: fieldValue // Add the actual value used for comparison
        };
        
        // Check if the field value is empty/undefined (incomplete)
        const isIncomplete = fieldValue === undefined || fieldValue === null || fieldValue === '';
        
        // If it's a required criterion and the value is incomplete, add to incomplete array
        if (criterion.required && isIncomplete) {
          incomplete.push(evaluatedCriterion);
          // Note: We don't add to weightedScore but we do add to totalWeight
          totalWeight += weight;
        } 
        // Otherwise, evaluate normally
        else {
          totalWeight += weight;
          
          // Check if the criterion is met
          const isMet = isIncomplete ? false : evaluateCriterion(criterion, fieldValue);
          
          if (isMet) {
            weightedScore += weight;
            matched.push(evaluatedCriterion);
          } else {
            missed.push(evaluatedCriterion);
            
            // If a required criterion has a value but is missed, the lender is disqualified
            if (criterion.required && !isIncomplete) {
              disqualified = true;
            }
          }
        }
        
        // Log the evaluation result for debugging
        console.log(`Criterion ${criterion.fieldId} ${criterion.operator} ${criterion.value}:`, 
                    `Value: "${fieldValue}" (${typeof fieldValue})`, 
                    `CriterionValue: "${criterion.value}" (${typeof criterion.value})`,
                    `Incomplete: ${isIncomplete}`,
                    `Met: ${isIncomplete ? 'Incomplete' : evaluateCriterion(criterion, fieldValue)}`);
      });
      
      // Calculate the final score (0-100)
      let finalScore = totalWeight > 0 ? Math.round((weightedScore / totalWeight) * 100) : 0;
      
      // If disqualified by a required criterion, score is 0
      if (disqualified) {
        finalScore = 0;
      }
      
      // Store the score and details
      scores[lender.lenderId] = {
        score: finalScore,
        matched,
        missed,
        incomplete,
        disqualified
      };
      
      console.log(`Final score for ${lender.name}: ${finalScore}%`);
    });
    
    // Update state with calculated scores
    setMatchScores(scores);
    
    // Sort lenders by score (highest first)
    const sortedLenders = [...lenders].sort((a, b) => {
      const scoreA = scores[a.id]?.score || 0;
      const scoreB = scores[b.id]?.score || 0;
      return scoreB - scoreA;
    });
    
    // Update lenders with sorted order
    setLenders(sortedLenders);
    
    // Record update time and show update alert
    setLastScoreUpdate(new Date());
    setShowUpdateAlert(true);
    
    // Hide update alert after 3 seconds
    setTimeout(() => {
      setShowUpdateAlert(false);
    }, 3000);
  };
  
  // Normalize a value to a number if possible
  const normalizeNumericValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    
    // Remove any non-numeric characters except decimal point
    // First remove currency symbols, commas, etc.
    const cleanValue = String(value).replace(/[^0-9.-]/g, '');
    const parsedValue = parseFloat(cleanValue);
    
    return isNaN(parsedValue) ? 0 : parsedValue;
  };
  
  // Evaluate if a criterion is met by the field value
  const evaluateCriterion = (criterion, fieldValue) => {
    // If field value is undefined or null, criterion is not met
    if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
      console.log(`Field value is empty for ${criterion.fieldId}`);
      return false;
    }
    
    const { fieldId, operator, value } = criterion;
    
    // Special case for Industry with "is_not" operator
    if (fieldId === 'Industry' && operator === 'is_not') {
      // value should be an array of excluded industries
      // If it's not an array yet, convert it to one
      const excludedIndustries = Array.isArray(value) ? value : value ? [value] : [];
      
      // Check if the field value is NOT in the excluded industries list
      // Return true if the industry is NOT excluded (meaning the criterion is met)
      const result = !excludedIndustries.includes(String(fieldValue));
      
      console.log(`Industry exclusion check for ${fieldValue}: Excluded industries: [${excludedIndustries.join(', ')}], Result: ${result}`);
      
      return result;
    }
    
    // Special case for State fields with "is_not" operator
    if ((fieldId === 'BusinessState' || fieldId === 'ContactState' || fieldId === 'Contact2State') && operator === 'is_not') {
      // value should be an array of excluded states
      // If it's not an array yet, convert it to one
      const excludedStates = Array.isArray(value) ? value : value ? [value] : [];
      
      // Check if the field value is NOT in the excluded states list
      // Return true if the state is NOT excluded (meaning the criterion is met)
      const result = !excludedStates.includes(String(fieldValue));
      
      console.log(`State exclusion check for ${fieldValue}: Excluded states: [${excludedStates.join(', ')}], Result: ${result}`);
      
      return result;
    }
    
    // Special case for "older_than_months" operator on date fields
    if (isDateComparisonField(fieldId) && operator === 'older_than_months') {
      const monthsOld = getMonthDifference(fieldValue);
      const requiredMonths = parseInt(value, 10);
      
      console.log(`Date age comparison for ${fieldId}: ${monthsOld} months old vs required ${requiredMonths} months`);
      
      return monthsOld >= requiredMonths;
    }
    
    // Special handling for numeric text fields
    const isNumeric = isNumericField(fieldId) || 
                     (operator === '>' || operator === '>=' || operator === '<' || operator === '<=');
    
    if (isNumeric) {
      // Normalize both values for comparison
      const numFieldValue = normalizeNumericValue(fieldValue);
      const numCriterionValue = normalizeNumericValue(value);
      
      console.log(`Numeric comparison for ${fieldId}: ${numFieldValue} ${operator} ${numCriterionValue}`);
      
      // Compare as numbers
      switch (operator) {
        case '>':
          return numFieldValue > numCriterionValue;
        case '>=':
          return numFieldValue >= numCriterionValue;
        case '=':
          return numFieldValue === numCriterionValue;
        case '<=':
          return numFieldValue <= numCriterionValue;
        case '<':
          return numFieldValue < numCriterionValue;
        default:
          // For non-comparison operators on numeric fields
          if (operator === 'contains') {
            return String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
          }
          return false;
      }
    }
    
    // Handle text operators for non-numeric fields
    switch (operator) {
      case '=':
        return String(fieldValue) === String(value);
      case 'contains':
        return String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
      default:
        return false;
    }
  };
  
  // Equipment information state
  const [equipmentItems, setEquipmentItems] = useState([]);
  
  // Toggle function for lender selection
  const toggleLenderSelection = (lenderId, submissionType = 'api') => {
    const lenderKey = submissionType === 'email' ? `email-${lenderId}` : lenderId;
    
    setSelectedLenders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lenderKey)) {
        newSet.delete(lenderKey);
      } else {
        newSet.add(lenderKey);
      }
      return newSet;
    });
  };

  // Master submit function
  const handleMasterSubmit = () => {
    if (selectedLenders.size === 0) {
      notifyError('Please select at least one lender to submit to.', 'No Lenders Selected');
      return;
    }
    setShowConfirmModal(true);
  };

  // Confirmed submit function
  const handleConfirmedSubmit = async () => {
    setShowConfirmModal(false);
    setMasterSubmitting(true);
    
    // Submit to all selected lenders IN PARALLEL (no change in speed)
    const submissionPromises = Array.from(selectedLenders).map(lenderKey => {
      const isEmail = lenderKey.startsWith('email-');
      const lenderId = isEmail ? lenderKey.replace('email-', '') : lenderKey;
      const submissionType = isEmail ? 'email' : 'api';
      
      return handleSubmitToLender(lenderId, submissionType);
    });
    
    try {
      // Use allSettled instead of all - still runs in parallel but handles failures better
      const results = await Promise.allSettled(submissionPromises);
      
      // Process results - some may have succeeded, some may have failed
      let successCount = 0;
      let failureCount = 0;
      
      results.forEach((result, index) => {
        const lenderKey = Array.from(selectedLenders)[index];
        
        if (result.status === 'fulfilled') {
          successCount++;
          console.log(`✅ ${lenderKey} submitted successfully`);
        } else {
          failureCount++;
          console.error(`❌ ${lenderKey} submission failed:`, result.reason);
        }
      });
      
      console.log(`📊 Batch submission complete: ${successCount} succeeded, ${failureCount} failed`);
      
      // Clear selections after all submissions are processed
      setSelectedLenders(new Set());
    } catch (error) {
      console.error('Unexpected error in batch submission:', error);
    } finally {
      setMasterSubmitting(false);
    }
  };
  
  // Clear equipment items from local storage on component mount
  useEffect(() => {
    if (financingType === 'equipment-financing' && opportunityId) {
      // Clear any previously saved equipment items
      localStorage.removeItem(`equipmentItems-${opportunityId}`);
      console.log(`Cleared equipment items from localStorage for opportunity ${opportunityId}`);
    }
  }, [financingType, opportunityId]);
  
  // Save equipment items to local storage only after submission
  const saveEquipmentItemsToLocalStorage = (items) => {
    if (financingType === 'equipment-financing' && opportunityId) {
      localStorage.setItem(`equipmentItems-${opportunityId}`, JSON.stringify(items));
      console.log(`Saved ${items.length} equipment items to localStorage for opportunity ${opportunityId}`);
    }
  };
  
  /**
   * Submit contact and business data to a lender
   * 
   * @param {string} lenderId - ID of the lender to submit to
   * @param {string} [submissionType='api'] - Type of submission ('api' or 'email')
   */
  const handleSubmitToLender = async (lenderId, submissionType = 'api') => {
    // Create a unique key for this lender submission based on type
    const lenderKey = submissionType === 'email' ? `email-${lenderId}` : lenderId;
    
    // Clear previous results and errors for this lender
    setResults(prev => ({ ...prev, [lenderKey]: null }));
    setErrors(prev => ({ ...prev, [lenderKey]: null }));
    
    // Get lender info
    const lender = lenders.find(l => l.id === lenderId);
    
    // Safety check - if we can't find the lender, log an error and return
    if (!lender) {
      console.error(`No lender found with ID: ${lenderId}`);
      notifyError(`Error: Lender with ID "${lenderId}" not found`, 'Submission Failed', 5000);
      return;
    }
    
    // Mark this lender as submitting
    setSubmittingLenders(prev => ({ ...prev, [lenderKey]: true }));
    
    // Show in-progress notification
    info(
      `Submitting to ${lender.name}${submissionType === 'email' ? ' via email' : ''}...`,
      'Submission Started',
      3000
    );
    
    try {
      // For backward compatibility
      setSubmitting(true);
      setCurrentLender(lender);
      
      // Check if required files are provided based on financing type
      if (financingType === 'equipment-financing') {
        // For equipment financing: need application, quotes, and credit files
        if (appFiles.length === 0 || quoteFiles.length === 0 || creditFiles.length === 0) {
          throw new Error('Application documents, quotes, and credit information are required for equipment financing.');
        }
      } else {
        // For working capital: need bank statements and application files
        if (bankFiles.length === 0 || appFiles.length === 0) {
          throw new Error('Bank statements and application documents are required for working capital.');
        }
      }
      
      // Check if equipment items are provided for equipment financing
      if (financingType === 'equipment-financing' && equipmentItems.length === 0) {
        throw new Error('At least one equipment item is required for equipment financing.');
      }
      
      // Create a copy of the business data and ensure the opportunity ID is included
      const enhancedBusinessData = { 
        ...businessData,
        // Add the opportunity ID explicitly to ensure it's available 
        // for the Salesforce submission
        OpportunityId: opportunityId,
        // Add financing type to the submission data
        FinancingType: financingType,
        // Add equipment items for equipment financing
        ...(financingType === 'equipment-financing' ? { EquipmentItems: equipmentItems } : {})
      };
      
      console.log(`Enhanced business data with opportunity ID from URL: ${opportunityId}, financingType: ${financingType}`);
      
      // Ensure we have valid contact data - create from business data if missing
      let validContactData = contactData;
      
      // If contactData is null/empty or missing essential fields, create it from business data
      if (!validContactData || !validContactData.FirstName || !validContactData.LastName) {
        console.log('Contact data is missing or incomplete, creating from business data...');
        validContactData = {
          FirstName: enhancedBusinessData.ContactFirstName || '',
          LastName: enhancedBusinessData.ContactLastName || '',
          Email: enhancedBusinessData.ContactEmail || '',
          Phone: enhancedBusinessData.ContactCellPhone || '',
          Title: enhancedBusinessData.ContactTitle || 'Owner',
          // Add other contact fields that might be needed
          MobilePhone: enhancedBusinessData.ContactCellPhone || '',
          // Include the original contactData properties if they exist
          ...(contactData || {})
        };
        console.log('Created contact data from business data:', validContactData);
      }
      
      // Submit to the lender API with appropriate files based on financing type
      const response = await submitToLender(
        validContactData,
        enhancedBusinessData,
        lenderId,
        bankFiles,
        appFiles,
        financingType,
        submissionType,
        financingType === 'equipment-financing' ? { quoteFiles, creditFiles } : {},
        dealNotes
      );
      
      // Store the successful result for this lender
      setResults(prev => ({ ...prev, [lenderKey]: response }));
      
      // For backward compatibility - only update result state for API submissions
      if (submissionType === 'api') {
        setResult(response);
      }
      
      // Show success notification with detailed message if available
      const notificationMessage = response.message || `Successfully submitted to ${lender.name}${submissionType === 'email' ? ' via email' : ''}`;
      const notificationTitle = response.isWarning ? 'Warning' : 'Success';
      
      if (response.isWarning) {
        warning(notificationMessage, notificationTitle, 8000);
      } else {
        success(notificationMessage, notificationTitle, 8000);
      }
      
      // Save equipment items to localStorage after successful submission
      if (financingType === 'equipment-financing') {
        saveEquipmentItemsToLocalStorage(equipmentItems);
      }
    } catch (err) {
      console.error(`Error submitting to ${lender.name}:`, err);
      
      // Format error message - Extract detailed information from the error response
      let errorMessage = 'An error occurred during submission';
      let detailedError = null;
      let isBusinessRuleDecline = false;
      
      // Check if there's a response object with data
      if (err.response) {
        console.log('Error response:', err.response);
        
        // Get status code
        const statusCode = err.response.status;
        
        // Try to get error details from response data
        if (err.response.data) {
          const responseData = err.response.data;
          
          // Use the specific message if available
          if (responseData.message) {
            errorMessage = responseData.message;
            
            // Check if this is a business rule decline (422 status with decline-related messages)
            if (statusCode === 422 && (
              errorMessage.includes('decline') ||
              errorMessage.includes('not supported') ||
              errorMessage.includes('not currently supported') ||
              errorMessage.includes('requirements not met') ||
              errorMessage.includes('ineligible') ||
              errorMessage.includes('restricted')
            )) {
              isBusinessRuleDecline = true;
            }
          }
          
          // Check for detailed validation errors (like from PIRS Capital)
          if (responseData.error && typeof responseData.error === 'object') {
            // Extract modelState errors for PIRS Capital
            if (responseData.error.modelState) {
              const modelStateErrors = [];
              
              // Collect all validation errors
              Object.entries(responseData.error.modelState).forEach(([field, errors]) => {
                if (Array.isArray(errors)) {
                  errors.forEach(err => modelStateErrors.push(`${field.replace(/^.*\./, '')}: ${err}`));
                }
              });
              
              if (modelStateErrors.length > 0) {
                detailedError = modelStateErrors.join(', ');
              }
            }
          }
        }
      } else if (err.message) {
        // Use error message directly if no response object
        errorMessage = err.message;
      }
      
      const finalErrorMessage = detailedError ? `${errorMessage}. ${detailedError}` : errorMessage;
      
      // Store the error for this lender with business rule decline flag
      setErrors(prev => ({ 
        ...prev, 
        [lenderKey]: finalErrorMessage,
        [`${lenderKey}_isDecline`]: isBusinessRuleDecline 
      }));
      
      // For backward compatibility - only update error state for API submissions
      if (submissionType === 'api') {
        setError(finalErrorMessage);
      }
      
      // Format the error message for notification
      const formatNotificationError = (message) => {
        // For modelState validation errors, make them more readable
        if (message.includes('Validation error:')) {
          const [prefix, details] = message.split('Validation error:');
          return `${prefix.trim()} Validation Error: ${details.trim()}`;
        }
        return message;
      };
      
      // Determine notification title based on error type
      const notificationTitle = isBusinessRuleDecline ? 'Warning' : 'Submission Failed';
      
      // Show error notification with appropriate title
      notifyError(
        formatNotificationError(`${isBusinessRuleDecline ? 'Notice' : 'Error'} submitting to ${lender.name}${submissionType === 'email' ? ' via email' : ''}: ${finalErrorMessage}`),
        notificationTitle,
        8000
      );
    } finally {
      // Mark this lender as no longer submitting
      setSubmittingLenders(prev => {
        const updated = { ...prev };
        delete updated[lenderKey];
        
        // For backward compatibility - only set submitting to false if this was the last active submission
        if (Object.keys(updated).length === 0) {
          setTimeout(() => setSubmitting(false), 0);
        }
        
        return updated;
      });
    }
  };
  


  // Validate equipment items for equipment financing
  const validateEquipmentItems = () => {
    if (financingType !== 'equipment-financing') return true;
    
    // Check if there are any equipment items
    if (equipmentItems.length === 0) {
      return false;
    }
    
    // Check each equipment item for required fields
    return equipmentItems.every(item => 
      item.equipmentType && 
      item.numberOfUnits && 
      item.costPerUnit
    );
  };
  
  // Check if equipment items are valid
  const equipmentItemsValid = validateEquipmentItems();
  
  return (
    <>
      {/* Equipment Information Section - Only show for equipment financing */}
      {financingType === 'equipment-financing' && (
        <div className="mb-4">
          <EquipmentInformation 
            equipmentItems={equipmentItems}
            setEquipmentItems={setEquipmentItems}
            disabled={disabled}
          />
          
          {/* Equipment validation warning */}
          {!equipmentItemsValid && (
            <Alert variant="warning" className="mt-3 mb-0">
              <FaExclamationTriangle className="me-1" size="0.8em" />
              Please add at least one equipment item with all required details before submitting to lenders.
            </Alert>
          )}
        </div>
      )}
      
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          {/* Debug info */}
          {console.log(`LenderSubmission render - lendersLoading: ${lendersLoading}, lenders.length: ${lenders.length}, preloadedLenders.length: ${preloadedLenders.length}`)}
          
          {/* Loading state for lenders */}
          {lendersLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 mb-0">Loading available lenders...</p>
            </div>
          )}
          
                  {/* No lenders available message - only show if loading is complete and we've processed the preloaded data */}
        {!lendersLoading && lenders.length === 0 && preloadedLenders.length === 0 && (
          <div className="text-center py-4">
            <Alert variant="info">
              <FaExclamationTriangle className="me-2" />
              No active lenders found for {financingType.replace('-', ' ')}. 
              Please check the Lender Settings page to activate lenders.
            </Alert>
          </div>
        )}
          
          {/* Show lender interface only when lenders are loaded */}
          {!lendersLoading && lenders.length > 0 && (
            <>
              {/* Optional: Match recalculation button when enabled */}
              {matchEnabled && !loadingMatch && lastScoreUpdate && (
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="d-inline-flex align-items-center py-1 px-2"
                    onClick={forceRecalculateScores}
                    disabled={loadingMatch}
                    style={{ fontSize: '0.75rem' }}
                  >
                    <FaSyncAlt size="0.8em" className={loadingMatch ? "spin-animation me-1" : "me-1"} />
                    {loadingMatch ? "Updating..." : "Refresh Match Scores"}
                  </Button>
                </div>
              )}
              
              {/* Tier-Based Lender Submissions */}
          {[1, 2, 3].map(tier => {
            const tierLenders = lenders
              .filter(lender => lender.tier === tier)
              .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name
            
            // Only show the tier section if there are lenders in it
            if (tierLenders.length === 0) return null;
            
            const tierLabels = {
              1: { name: "Tier 1 Lenders", description: "Primary lenders - highest priority", variant: "success" },
              2: { name: "Tier 2 Lenders", description: "Secondary lenders - medium priority", variant: "warning" },
              3: { name: "Tier 3 Lenders", description: "Backup lenders - lowest priority", variant: "secondary" }
            };
            
            return (
              <Card key={tier} className="mb-4 border-0 shadow-sm">
                <Card.Header className={`bg-light text-muted py-2 border-${tierLabels[tier].variant}`}>
                  <div className="d-flex align-items-center justify-content-between">
                    <h5 className="mb-0 fs-6 d-flex align-items-center">
                      <span className={`badge bg-${tierLabels[tier].variant} me-2`}>{tier}</span>
                      {tierLabels[tier].name}
                    </h5>
                    <small className="text-muted">{tierLabels[tier].description}</small>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row xs={1} sm={2} md={3} lg={4} className="g-3">
                    {tierLenders.map(lender => {
                      // Determine submission type and result/error key
                      const submissionType = lender.emailOnly ? 'email' : 'api';
                      const lenderKey = lender.emailOnly ? `email-${lender.id}` : lender.id;
                      
                      // Get match data for this lender
                      const lenderScore = matchScores[lender.id] || { 
                        score: 0, 
                        matched: [], 
                        missed: [], 
                        incomplete: [], 
                        disqualified: false
                      };
                      
                      return (
                        <Col key={lenderKey}>
                          <div className="d-flex flex-column h-100">
                            <EnhancedLenderButton
                              lender={lender}
                              matchData={lenderScore}
                              isSubmitting={submittingLenders[lenderKey]}
                              disabled={disabled || (financingType === 'equipment-financing' && !equipmentItemsValid)}
                              onSubmit={() => toggleLenderSelection(lender.id, submissionType)}
                              submissionType={submissionType}
                              variant={submissionType === 'email' ? "outline-secondary" : "outline-primary"}
                              tier={tier}
                              isSelected={selectedLenders.has(lenderKey)}
                              isToggleMode={true}
                            />
                            
                            {/* Display result for this lender */}
                            {results[lenderKey] && (
                              <Alert variant={results[lenderKey].isWarning ? "warning" : "success"} className="p-2 small mb-0">
                                {results[lenderKey].isWarning ? (
                                  <FaExclamationTriangle className="me-1" size="0.8em" />
                                ) : (
                                  <FaCheck className="me-1" size="0.8em" />
                                )}
                                {/* Use the detailed message from server if available, otherwise fallback to default */}
                                {results[lenderKey].message || (submissionType === 'email' ? 'Email sent successfully' : 'Submitted successfully')}
                                {results[lenderKey].submissionId && !results[lenderKey].message?.includes('ID:') && (
                                  <div className="mt-1 text-muted" style={{ fontSize: '0.9em' }}>
                                    ID: <strong>{results[lenderKey].submissionId}</strong>
                                  </div>
                                )}
                              </Alert>
                            )}
                            
                            {/* Display error for this lender */}
                            {errors[lenderKey] && (
                              <Alert variant={errors[`${lenderKey}_isDecline`] ? "warning" : "danger"} className="p-2 small mb-0">
                                <FaExclamationTriangle className="me-1" size="0.8em" />
                                <div>
                                  <div><strong>{errors[`${lenderKey}_isDecline`] ? 'Warning:' : (submissionType === 'email' ? 'Email Failed:' : 'Submission Failed:')}</strong></div>
                                  <div>
                                    {/* Format error message for better readability */}
                                    {errors[lenderKey].split('. ').map((part, index) => (
                                      <p key={index} className={index > 0 ? 'mt-1 mb-0' : 'mb-0'}>
                                        {/* Highlight validation errors */}
                                        {part.includes(':') && part.includes('Validation error') ? (
                                          <>
                                            <span>{part.split(':')[0]}: </span>
                                            <strong className="text-danger">{part.split(':').slice(1).join(':')}</strong>
                                          </>
                                        ) : (
                                          part
                                        )}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              </Alert>
                            )}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Card.Body>
              </Card>
            );
                      })}
          
            {/* Master Submit Button */}
            {lenders.length > 0 && (
              <div className="d-flex justify-content-end mt-4 mb-3">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleMasterSubmit}
                  disabled={disabled || selectedLenders.size === 0 || masterSubmitting || (financingType === 'equipment-financing' && !equipmentItemsValid)}
                  className="px-4"
                >
                  {masterSubmitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Submitting to {selectedLenders.size} lender{selectedLenders.size !== 1 ? 's' : ''}...
                    </>
                  ) : (
                    <>
                      Submit to Selected Lenders ({selectedLenders.size})
                    </>
                  )}
                </Button>
              </div>
            )}
          
            {/* Backward compatibility display */}
            {(result || error) && !Object.keys(results).length && !Object.keys(errors).length && (
              <Alert variant={error ? 'danger' : 'success'} className="mt-3">
                {error ? (
                  <>
                    <FaExclamationTriangle className="me-2" />
                    {error}
                  </>
                ) : (
                  <>
                    <FaCheck className="me-2" />
                    Successfully submitted to {currentLender?.name}.
                    {result.submissionId && (
                      <div className="mt-1">
                        Submission ID: <strong>{result.submissionId}</strong>
                      </div>
                    )}
                  </>
                )}
              </Alert>
            )}
            </>
          )}
        </Card.Body>
      </Card>
      
      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You are about to submit to the following {selectedLenders.size} lender{selectedLenders.size !== 1 ? 's' : ''}:</p>
          <ul className="mb-3">
            {Array.from(selectedLenders).map(lenderKey => {
              const isEmail = lenderKey.startsWith('email-');
              const lenderId = isEmail ? lenderKey.replace('email-', '') : lenderKey;
              const lender = lenders.find(l => l.id === lenderId);
              return (
                <li key={lenderKey}>
                  <strong>{lender?.name}</strong> {isEmail && <span className="text-muted">(via email)</span>}
                </li>
              );
            })}
          </ul>
          <p className="text-muted">Are you sure you want to proceed?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmedSubmit}>
            Confirm & Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LenderSubmission; 